import type { IPlan, RevealApiTierLevels } from './Plan'
import type { IUser } from './User'

export type StripeSubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'incomplete'
  | 'incomplete_expired'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'paused'
  | 'unknown'

export const PERSONYZE_REGISTRATION_SOURCE = 'personyze'

export const WELCOME_FORM_CUSTOMER_FIELDS = [
  'name',
  'website',
  'country',
  'company_size',
] as const satisfies readonly (keyof ICustomer)[]
export const WELCOME_FORM_USER_FIELDS = ['first_name', 'last_name'] as const satisfies readonly (keyof IUser)[]
export const WELCOME_FORM_FIELDS = [...WELCOME_FORM_CUSTOMER_FIELDS, ...WELCOME_FORM_USER_FIELDS]

export type PersonyzeTrialsRunResult = {
  startCandidates: number
  started: number
  trialCandidates: number
  endedEarly: number
  // Per-customer failures; the pass continues past them and the caller decides what that means.
  failed: number
}

export type CreatePersonyzeAccountParams = { email: string; name: string; websites: string[] }

// `created` lets the proxy answer 201 or 200; the account fields are identical either way.
export type PersonyzeAccountResult = {
  customer_id: number
  api_key: string
  created: boolean
}

// Named for what a partner would do about it, not for Stripe's vocabulary, which we would
// otherwise be unable to change without breaking them.
export type PersonyzeCustomerStatus = 'not_started' | 'trialing' | 'active' | 'cancelling' | 'past_due' | 'expired'

export type PersonyzeCustomerUsage = {
  customer_id: number
  status: PersonyzeCustomerStatus
  trial_ends_at: string | null
  tier: RevealApiTierLevels | null
  usage: {
    api_calls: number
    api_calls_limit: number
    identified_companies: number
    identified_companies_limit: number
  }
}

export interface ICustomer {
  id: number
  name: string
  client_id: string
  code_status: string
  stripe_subscription_status: StripeSubscriptionStatus
  company_size: string | null
  default_currency: string | null
  country: string | null
  purpose: string | null
  phone_number: string | null
  website: string
  is_partner: boolean
  registration_source: string
  plan_id: number
  tech: string[]
  mfa_setup_required: boolean
  registration_purpose: string[]
  payment_method: 'card' | 'invoice'
  is_beta: boolean
  is_sales_led: boolean
  tier: number
  ga4_client_id: string | null

  created_at: Date
  updated_at: Date
  deleted_at: Date | null

  plan?: IPlan
}

export interface MatchedCustomer extends Pick<ICustomer, 'id' | 'name' | 'website'> {}
